import { useTranslation } from 'react-i18next'

import { useAuth } from '@enechain/auth-js'
import {
  SideMenuLayoutMenuItem,
  SideMenuLayoutPopOverMenuItem,
  SideMenuLayoutUserMenu,
} from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'

import { clearGridCardLayouts } from '~/hooks/useGridCardLayout'

// 個人設定 link は design HTML 踏襲で TopMenu の システム管理直後 に top-level link
// として配置 (= BottomMenu からは撤去)。 BottomMenu には user menu (= logout 等) と
// ヘルプセンター導線を置く (= design HTML の sidebar-footer 相当)。

/**
 * ヘルプセンター (Zendesk) の URL。遷移先の言語は表示言語に連動させる
 * (spec common/shared-components.md AC-SC-008: 日本語は /hc/ja、英語は /hc/en-us)。
 *
 * 外部サイトなので `~/routes/routePaths` には載せない (アプリ内 route ではない)。
 */
const HELP_CENTER_URL_JA = 'https://ebalance.zendesk.com/hc/ja'
const HELP_CENTER_URL_EN = 'https://ebalance.zendesk.com/hc/en-us'

export const BottomMenu = (): React.ReactElement => {
  const { i18n, t } = useTranslation()
  const { logout, user } = useAuth()
  // 英語以外 (日本語・未知の言語) は日本語のヘルプへ倒す。判定は他画面の
  // 英語出し分けと同じ `startsWith('en')`
  const helpCenterUrl = i18n.language.startsWith('en')
    ? HELP_CENTER_URL_EN
    : HELP_CENTER_URL_JA

  return (
    <>
      {/* ヘルプセンター導線 (2026-09-01 デザイナー指摘)。 eSquare Live と同じく
          **ユーザープロフィール導線の上**に置く。 サイドメニューの他項目と同じ
          `SideMenuLayoutMenuItem` を使うので、 折りたたみ時はアイコンのみ・
          展開時はラベル付きになる (= 他の導線と挙動が揃う)。

          外部サイトなので `as="a"` + `target="_blank"` で別タブへ開き、
          `rel="noopener noreferrer"` を付ける。 外部リンクである旨は DS 自前の
          `itemType="external"` (ラベル右端に外部リンクアイコン) で示す。

          **[DS差異] `isLink` は使わない。** DS は `isLink` が真のとき `as` を
          無視して chakra `Link` に差し替え、 下線の抑止を `sx` で行っている
          (`SideMenuLayoutMenuItem`)。 しかし Chakra v3 で `sx` は廃止されており
          この抑止は効かない。 結果、 (a) 他項目 (chakra `Button`) と描画経路が
          変わって中央寄せになり、 (b) DS グローバルテーマの
          `a { _hover: { textDecoration: 'underline' } }` がそのまま出る
          (2026-09-01 デザイナー指摘「左寄せに」「ホバーで下線を出さない」)。
          `as="a"` だけにすると他項目と同じ Button 経路に戻り、 DS が指定する
          `justifyContent: 'flex-start'` が効いて左寄せになる。 */}
      <SideMenuLayoutMenuItem
        as="a"
        IconLeft={FaCircleQuestionRegular}
        // DS の menu item は**折りたたみ時に `label` を描画せず**、 アイコンは
        // `aria-hidden` なので読み上げ名が空になる (DS の全項目に共通)。 他項目は
        // 展開すれば名前が付くが、 この項目は外部リンクで行き先が URL でしか
        // 判別できないため、 折りたたみ時も名前を持たせる。
        aria-label={t('common:nav.helpCenter')}
        // 残るグローバルテーマ由来の下線だけを消す。 `_hover` で渡すと DS が
        // 指定している hover の背景色・文字色ごと置き換わってしまうため使わない。
        css={{ '&:hover': { textDecoration: 'none' } }}
        href={helpCenterUrl}
        itemType="external"
        label={t('common:nav.helpCenter')}
        rel="noopener noreferrer"
        target="_blank"
      />
      <SideMenuLayoutUserMenu
        popOverMenu={
          <SideMenuLayoutPopOverMenuItem
            label={t('common:nav.logout')}
            onClick={() => {
              // 組織のマスタ由来の識別子 (発電 BG コード) を含む保存値は、
              // localStorage に残すとログアウト後も同一ブラウザから読めるため
              // セッション終了時に破棄する (`clearGridCardLayouts` の doc 参照)。
              clearGridCardLayouts()
              void logout()
            }}
          />
        }
        userIconSrc={user?.picture}
        userName={user?.name ?? ''}
      />
    </>
  )
}
